/**
 * The device's own share sheet, where there is one.
 *
 * Markup contract: a `[data-web-share]` button, `hidden` in the markup, carrying `data-share-url`,
 * optionally `data-share-title`, `data-share-text` and `data-share-image`. Where `navigator.share`
 * exists — a phone, mostly — the button is revealed; a click opens the sheet with the link, and with
 * the IMAGE as a file when one is named and the platform takes files. That is the only road to
 * Instagram, Signal or Messages: none of them takes a URL parameter, all of them take a shared file.
 *
 * Without the image, or when the fetch or the file refusal fails, the link alone is shared: a card
 * that travels as a link still unfolds through its og:image. A dismissed sheet is not an error.
 *
 * Delegated on `document` for the same reason copy-value.js is: the element it serves is re-rendered
 * with the answer.
 */
function reveal() {
	if (!navigator.share) {
		return;
	}
	document.querySelectorAll('[data-web-share][hidden]').forEach((button) => {
		button.hidden = false;
	});
}

async function fileOf(url, name) {
	const response = await fetch(url, { credentials: 'omit' });
	if (!response.ok) {
		throw new Error(`image ${response.status}`);
	}
	const blob = await response.blob();
	return new File([blob], name, { type: blob.type });
}

document.addEventListener('click', async (event) => {
	const button = event.target.closest('[data-web-share]');
	if (!button || !navigator.share) {
		return;
	}
	event.preventDefault();
	const data = { url: button.dataset.shareUrl || location.href };
	if (button.dataset.shareTitle) {
		data.title = button.dataset.shareTitle;
	}
	if (button.dataset.shareText) {
		data.text = button.dataset.shareText;
	}
	if (button.dataset.shareImage && navigator.canShare) {
		try {
			const file = await fileOf(button.dataset.shareImage, button.dataset.shareFilename || 'card.jpg');
			if (navigator.canShare({ files: [file] })) {
				data.files = [file];
			}
		} catch (noFile) {
			// The link travels alone.
		}
	}
	try {
		await navigator.share(data);
		if (typeof window.gtag === 'function') {
			window.gtag('event', 'share', { method: 'share_sheet', content_type: data.files ? 'image' : 'link' });
		}
	} catch (dismissed) {
		// The sheet was closed, or the platform refused: nothing to say.
	}
});

reveal();
document.addEventListener('DOMContentLoaded', reveal);
